/**
 * QuickTimerButton Component
 *
 * A quick action button for starting/viewing timers on the dashboard.
 * Shows elapsed time when a timer is active, otherwise shows a start button.
 */

import { useState } from 'react';
import { useTimers, ActiveTimer } from '../stores/useTimers';
import { useElapsedTime } from '../hooks/useElapsedTime';

interface QuickTimerButtonProps {
  babyId: string;
  type: 'FEED' | 'SLEEP' | 'TUMMY' | 'KICK_COUNT' | 'CONTRACTION';
  onTimerStart?: (timerId: string) => void;
  onTimerClick?: (timer: ActiveTimer) => void;
}

export default function QuickTimerButton({
  babyId,
  type,
  onTimerStart,
  onTimerClick,
}: QuickTimerButtonProps) {
  const { getActiveTimer, startFeedTimer, startSleepTimer, startTummyTimer, startKickCountTimer, startContractionTimer } = useTimers();
  const [showSideSelector, setShowSideSelector] = useState(false);
  const [showSleepTypeSelector, setShowSleepTypeSelector] = useState(false);

  const activeTimer = getActiveTimer(babyId, type);
  const { formatted } = useElapsedTime(activeTimer?.startTime || null, {
    totalPausedTime: activeTimer?.totalPausedTime,
    isPaused: activeTimer?.isPaused,
    pausedAt: activeTimer?.pausedAt,
  });

  const icon = type === 'FEED' ? '🍼' : type === 'SLEEP' ? '😴' : type === 'TUMMY' ? '🏋️' : type === 'KICK_COUNT' ? '🦶' : '⏱️';
  const label = type === 'FEED' ? 'Feed' : type === 'SLEEP' ? 'Sleep' : type === 'TUMMY' ? 'Tummy' : type === 'KICK_COUNT' ? 'Kick Count' : 'Contraction';

  const handleClick = async () => {
    if (activeTimer) {
      if (onTimerClick) {
        onTimerClick(activeTimer);
      }
      return;
    }

    if (type === 'FEED') {
      setShowSideSelector(true);
    } else if (type === 'SLEEP') {
      setShowSleepTypeSelector(true);
    } else if (type === 'TUMMY') {
      const timerId = await startTummyTimer(babyId);
      if (onTimerStart) onTimerStart(timerId);
    } else if (type === 'KICK_COUNT') {
      const timerId = await startKickCountTimer(babyId);
      if (onTimerStart) onTimerStart(timerId);
    } else if (type === 'CONTRACTION') {
      const timerId = await startContractionTimer(babyId);
      if (onTimerStart) onTimerStart(timerId);
    }
  };

  const handleStartFeed = async (side: 'left' | 'right') => {
    const timerId = await startFeedTimer(babyId, side);
    setShowSideSelector(false);
    if (onTimerStart) {
      onTimerStart(timerId);
    }
  };

  const handleStartSleep = async (sleepType: 'nap' | 'night') => {
    const timerId = await startSleepTimer(babyId, sleepType);
    setShowSleepTypeSelector(false);
    if (onTimerStart) {
      onTimerStart(timerId);
    }
  };

  // Side selector modal for breastfeeding
  if (showSideSelector) {
    return (
      <div className="relative">
        <div className="absolute inset-0 z-10">
          <div
            className="fixed inset-0"
            onClick={() => setShowSideSelector(false)}
          />
        </div>
        <div className="relative z-20 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
            Which side first?
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => handleStartFeed('left')}
              className="flex-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 py-2 px-3 rounded-lg font-medium hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
            >
              Left
            </button>
            <button
              onClick={() => handleStartFeed('right')}
              className="flex-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 py-2 px-3 rounded-lg font-medium hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
            >
              Right
            </button>
          </div>
          <button
            onClick={() => setShowSideSelector(false)}
            className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Sleep type selector modal
  if (showSleepTypeSelector) {
    return (
      <div className="relative">
        <div className="absolute inset-0 z-10">
          <div
            className="fixed inset-0"
            onClick={() => setShowSleepTypeSelector(false)}
          />
        </div>
        <div className="relative z-20 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center">
            Sleep type
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => handleStartSleep('nap')}
              className="flex-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 py-2 px-3 rounded-lg font-medium hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
            >
              Nap
            </button>
            <button
              onClick={() => handleStartSleep('night')}
              className="flex-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 py-2 px-3 rounded-lg font-medium hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
            >
              Night
            </button>
          </div>
          <button
            onClick={() => setShowSleepTypeSelector(false)}
            className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Main button
  const activeClass = type === 'TUMMY'
    ? 'bg-teal-100 dark:bg-teal-900/30 ring-2 ring-teal-500 dark:ring-teal-400'
    : type === 'KICK_COUNT'
    ? 'bg-pink-100 dark:bg-pink-900/30 ring-2 ring-pink-500 dark:ring-pink-400'
    : type === 'CONTRACTION'
    ? 'bg-rose-100 dark:bg-rose-900/30 ring-2 ring-rose-500 dark:ring-rose-400'
    : 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500 dark:ring-primary-400';
  return (
    <button
      onClick={handleClick}
      className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
        activeTimer
          ? activeClass
          : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <span className={`text-2xl ${activeTimer && !activeTimer.isPaused ? 'animate-pulse' : ''}`}>
        {icon}
      </span>
      {activeTimer ? (
        <>
          <span className="mt-1 font-mono font-semibold text-primary-700 dark:text-primary-300">
            {formatted}
          </span>
          {activeTimer.isPaused && (
            <span className="text-xs font-semibold bg-amber-400 dark:bg-amber-500 text-amber-900 px-1.5 py-0.5 rounded uppercase">
              PAUSED
            </span>
          )}
          {type === 'FEED' && activeTimer.side && !activeTimer.isPaused && (
            <span className="text-xs text-primary-600 dark:text-primary-400 uppercase">
              {activeTimer.side}
            </span>
          )}
          {type === 'TUMMY' && activeTimer.headUpActive && (
            <span className="text-xs text-teal-600 dark:text-teal-400">↑ Head Up</span>
          )}
        </>
      ) : (
        <>
          <span className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-300">
            Start {label}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Timer</span>
        </>
      )}
    </button>
  );
}

/**
 * Compact version of the timer button for use in tight spaces
 */
export function CompactTimerButton({
  babyId,
  type,
  onTimerClick,
}: Omit<QuickTimerButtonProps, 'onTimerStart'>) {
  const { getActiveTimer } = useTimers();
  const activeTimer = getActiveTimer(babyId, type);
  const { formatted } = useElapsedTime(activeTimer?.startTime || null, {
    totalPausedTime: activeTimer?.totalPausedTime,
    isPaused: activeTimer?.isPaused,
    pausedAt: activeTimer?.pausedAt,
  });

  if (!activeTimer) return null;

  return (
    <button
      onClick={() => onTimerClick?.(activeTimer)}
      className="inline-flex items-center gap-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-1 rounded-full text-sm"
    >
      <span className={activeTimer.isPaused ? '' : 'animate-pulse'}>{type === 'FEED' ? '🍼' : type === 'SLEEP' ? '😴' : type === 'TUMMY' ? '🏋️' : type === 'KICK_COUNT' ? '🦶' : '⏱️'}</span>
      <span className="font-mono font-medium">{formatted}</span>
      {activeTimer.isPaused && (
        <span className="text-xs font-semibold bg-amber-400 dark:bg-amber-500 text-amber-900 px-1 rounded uppercase">
          ⏸
        </span>
      )}
    </button>
  );
}
